import { jsPDF } from 'jspdf';
import {
  defaultPdsPdfFilename,
  employerLabels,
  filledBeneficiaries,
  fmtCommunicationFrequency,
  fmtCountry,
  fmtDate,
  fmtDelivery,
  fmtEmploymentStatus,
  fmtOccupation,
  fmtText,
  fmtYesNo,
  formatAddress,
  formatCents,
  hasEmployerDetails,
  householdHasSpouse,
  secondaryTcpHasData,
  sectionKey,
  sumCents,
} from './pdsFormat';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 48;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

export { defaultPdsPdfFilename };

/**
 * Shared with `checkinPdfExport.js`, plus `paragraph()` for the explanatory
 * copy the PDS carries and `table()` for beneficiary lists.
 */
class PdfWriter {
  constructor(doc) {
    this.doc = doc;
    this.y = MARGIN;
  }

  ensureSpace(height) {
    if (this.y + height > PAGE_HEIGHT - MARGIN) {
      this.doc.addPage();
      this.y = MARGIN;
      return true;
    }
    return false;
  }

  title(text) {
    this.ensureSpace(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(18);
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(text, MARGIN, this.y + 14);
    this.y += 22;
  }

  subtitle(text) {
    if (!text) return;
    this.ensureSpace(14);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
    this.doc.setTextColor(108, 117, 125);
    this.doc.text(text, MARGIN, this.y + 10);
    this.y += 14;
  }

  /** Banner introducing a person's block of sections. */
  personHeader(text) {
    this.ensureSpace(30);
    this.doc.setFillColor(38, 93, 154);
    this.doc.roundedRect(MARGIN, this.y, CONTENT_WIDTH, 22, 3, 3, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(12);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(text, MARGIN + 10, this.y + 15);
    this.y += 30;
  }

  sectionHeader(number, title) {
    this.ensureSpace(24);
    this.doc.setFillColor(245, 247, 249);
    this.doc.roundedRect(MARGIN, this.y, CONTENT_WIDTH, 20, 3, 3, 'F');

    this.doc.setFillColor(38, 93, 154);
    this.doc.circle(MARGIN + 12, this.y + 10, 6, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(String(number), MARGIN + 12, this.y + 13, { align: 'center' });

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(11);
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(title, MARGIN + 24, this.y + 13);
    this.y += 24;
  }

  row(label, value) {
    const leftWidth = 200;
    const rightWidth = CONTENT_WIDTH - leftWidth;
    const leftLines = this.doc.splitTextToSize(String(label), leftWidth - 8);
    const rightLines = this.doc.splitTextToSize(String(value), rightWidth - 8);
    const lines = Math.max(leftLines.length, rightLines.length);
    const rowHeight = lines * 11 + 4;

    this.ensureSpace(rowHeight + 2);

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(80, 87, 94);
    this.doc.text(leftLines, MARGIN + 4, this.y + 9);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(rightLines, MARGIN + leftWidth + 4, this.y + 9);

    this.doc.setDrawColor(235, 238, 241);
    this.doc.setLineWidth(0.3);
    this.doc.line(MARGIN, this.y + rowHeight, MARGIN + CONTENT_WIDTH, this.y + rowHeight);

    this.y += rowHeight;
  }

  /** Wrapped body copy, used for the TCP and beneficiary explainers. */
  paragraph(text, { italic = false } = {}) {
    const clean = String(text || '').trim();
    if (!clean) return;
    this.doc.setFont('helvetica', italic ? 'italic' : 'normal');
    this.doc.setFontSize(8.5);
    this.doc.setTextColor(108, 117, 125);
    const lines = this.doc.splitTextToSize(clean, CONTENT_WIDTH - 8);
    this.ensureSpace(lines.length * 10 + 8);
    this.doc.text(lines, MARGIN + 4, this.y + 8);
    this.y += lines.length * 10 + 8;
  }

  /**
   * Simple grid. `columns` is [{ header, width, align }]; widths are in
   * points and should sum to CONTENT_WIDTH. The header row is redrawn
   * whenever the table spills onto a new page.
   */
  table(columns, rows, { caption } = {}) {
    if (!rows.length) return;

    const drawHeader = () => {
      this.doc.setFillColor(238, 241, 244);
      this.doc.rect(MARGIN, this.y, CONTENT_WIDTH, 16, 'F');
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(8);
      this.doc.setTextColor(80, 87, 94);
      let x = MARGIN;
      columns.forEach((col) => {
        this.doc.text(col.header, x + 4, this.y + 11);
        x += col.width;
      });
      this.y += 16;
    };

    if (caption) {
      this.ensureSpace(14);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(9);
      this.doc.setTextColor(33, 37, 41);
      this.doc.text(caption, MARGIN + 4, this.y + 9);
      this.y += 14;
    }

    this.ensureSpace(34);
    drawHeader();

    rows.forEach((cells) => {
      const wrapped = columns.map((col, i) => (
        this.doc.splitTextToSize(String(cells[i] ?? ''), col.width - 8)
      ));
      const lineCount = Math.max(...wrapped.map((w) => w.length));
      const rowHeight = lineCount * 10 + 6;

      if (this.ensureSpace(rowHeight)) drawHeader();

      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8.5);
      this.doc.setTextColor(33, 37, 41);
      let x = MARGIN;
      wrapped.forEach((lines, i) => {
        this.doc.text(lines, x + 4, this.y + 9);
        x += columns[i].width;
      });

      this.doc.setDrawColor(235, 238, 241);
      this.doc.setLineWidth(0.3);
      this.doc.line(MARGIN, this.y + rowHeight, MARGIN + CONTENT_WIDTH, this.y + rowHeight);
      this.y += rowHeight;
    });

    this.y += 6;
  }

  notes(text) {
    const clean = String(text || '').trim();
    if (!clean) return;
    const lines = this.doc.splitTextToSize(clean, CONTENT_WIDTH - 16);
    const height = lines.length * 11 + 16;

    this.ensureSpace(height + 8);

    this.doc.setFillColor(255, 249, 232);
    this.doc.roundedRect(MARGIN, this.y + 2, CONTENT_WIDTH, height, 3, 3, 'F');
    this.doc.setFillColor(255, 193, 7);
    this.doc.rect(MARGIN, this.y + 2, 3, height, 'F');

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.doc.setTextColor(130, 97, 0);
    this.doc.text('Notes', MARGIN + 8, this.y + 12);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(lines, MARGIN + 8, this.y + 24);

    this.y += height + 10;
  }

  footer() {
    const pages = this.doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i += 1) {
      this.doc.setPage(i);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.doc.setTextColor(130, 138, 145);
      this.doc.text(
        'Social Security Numbers are collected at the Getting Started meeting, not on this form.',
        MARGIN, PAGE_HEIGHT - 20,
      );
      this.doc.text(
        `Page ${i} of ${pages}`,
        PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 20, { align: 'right' },
      );
    }
  }
}

const BENEFICIARY_COLUMNS = [
  { header: '%', width: 44 },
  { header: 'Relationship', width: 84 },
  { header: 'Full Name', width: 118 },
  { header: 'Date of Birth', width: 70 },
  { header: 'Mailing Address', width: CONTENT_WIDTH - 44 - 84 - 118 - 70 },
];

function beneficiaryRows(rows) {
  return filledBeneficiaries(rows).map((r) => ([
    fmtText(r.pct),
    fmtText(r.relationship),
    fmtText(r.full_name),
    fmtDate(r.dob),
    formatAddress(r.address),
  ]));
}

function writeBeneficiaryGroup(w, label, rows) {
  const entries = beneficiaryRows(rows);
  if (!entries.length) {
    w.row(`${label} beneficiaries`, 'None listed');
    return;
  }
  const total = sumCents(rows);
  const totalNote = total === 10000
    ? `${label} beneficiaries — total 100.00%`
    : `${label} beneficiaries — total ${formatCents(total)}% (does not equal 100.00%)`;
  w.table(BENEFICIARY_COLUMNS, entries, { caption: totalNote });
}

/** Write one person's five sections. Returns the next section number. */
function writePerson(w, formData, prefix, heading, startNumber, spousePresent) {
  const get = (suffix) => formData[sectionKey(prefix, suffix)] || {};
  let n = startNumber;

  w.personHeader(heading);

  const contact = get('contact');
  w.sectionHeader(n, 'Contact Information');
  w.row('Full legal name', fmtText(contact.full_name));
  w.row('Preferred name', fmtText(contact.preferred_name));
  w.row('Known by another name', fmtYesNo(contact.known_by_other_name));
  if (contact.known_by_other_name === 'yes') {
    w.row('Other name', fmtText(contact.other_name));
  }
  w.row('Date of birth', fmtDate(contact.date_of_birth));
  w.row('Primary phone', fmtText(contact.phone));
  w.row('Email', fmtText(contact.email));
  w.row('Legal / home address', formatAddress(contact.address));
  w.row("Mother's maiden name", fmtText(contact.mothers_maiden_name));
  w.row('Security question', fmtText(contact.security_question));
  w.row('Security answer', fmtText(contact.security_answer));
  w.row('Country(ies) of citizenship', fmtCountry(contact.citizenship, contact.citizenship_other));
  w.row('Country(ies) of legal residence', fmtCountry(contact.residence, contact.residence_other));
  w.row('Country(ies) of birth', fmtCountry(contact.birth_country, contact.birth_country_other));
  w.notes(contact.notes);
  n += 1;

  const employment = get('employment');
  w.sectionHeader(n, 'Employment');
  w.row('Employment status', fmtEmploymentStatus(employment.status));
  if (hasEmployerDetails(employment)) {
    const labels = employerLabels(employment);
    w.row(labels.name, fmtText(employment.employer_name));
    w.row(labels.address, formatAddress(employment.employer_address));
  }
  if (employment.occupation) {
    w.row('Occupation', fmtOccupation(employment));
  }
  w.notes(employment.notes);
  n += 1;

  const compliance = get('compliance');
  w.sectionHeader(n, 'Compliance & Reporting');
  w.row(
    'Affiliated with an exchange, FINRA, or a municipal securities broker-dealer',
    fmtYesNo(compliance.exchange_affiliated),
  );
  if (compliance.exchange_affiliated === 'yes') {
    w.row('Firm and relationship', fmtText(compliance.exchange_affiliated_details));
  }
  w.row(
    'Director, 10% shareholder, or policy-making officer of a public company',
    fmtYesNo(compliance.public_company_insider),
  );
  if (compliance.public_company_insider === 'yes') {
    w.row('Company and ticker', fmtText(compliance.public_company_insider_details));
  }
  w.row('Paperless regulatory documents', fmtDelivery(compliance.paperless_delivery));
  w.row('Quarterly reports via client portal', fmtDelivery(compliance.quarterly_reports_portal));
  w.row(
    'How often they want to hear from us',
    fmtCommunicationFrequency(compliance.communication_frequency),
  );
  w.notes(compliance.notes);
  n += 1;

  const tcp = get('trusted_contact');
  w.sectionHeader(n, 'Trusted Contact Person');
  w.paragraph(
    'A Trusted Contact Person may be contacted if we have concerns about the '
    + "client's health, well-being, or welfare, or if we cannot reach them "
    + 'about important account matters. They have no authority over the '
    + 'accounts and no access to account information.',
    { italic: true },
  );
  w.row('Full name', fmtText(tcp.full_name));
  w.row('Relationship to client', fmtText(tcp.relationship));
  w.row('Phone', fmtText(tcp.phone));
  w.row('Email', fmtText(tcp.email));
  w.row('Physical address', formatAddress(tcp.address));
  if (secondaryTcpHasData(tcp)) {
    w.row('Secondary — full name', fmtText(tcp.secondary.full_name));
    w.row('Secondary — relationship to client', fmtText(tcp.secondary.relationship));
    w.row('Secondary — phone', fmtText(tcp.secondary.phone));
    w.row('Secondary — email', fmtText(tcp.secondary.email));
    w.row('Secondary — physical address', formatAddress(tcp.secondary.address));
  }
  w.notes(tcp.notes);
  n += 1;

  const bene = get('beneficiaries');
  const skipPrimary = spousePresent && bene.spouse_is_sole_primary === 'yes';
  w.sectionHeader(n, 'Beneficiaries');
  if (bene.same_as_primary === true) {
    w.row('Beneficiaries', "Copied from the client's own designations");
  }
  if (spousePresent) {
    w.row('Spouse/partner is the 100% primary beneficiary', fmtYesNo(bene.spouse_is_sole_primary));
  }
  if (!skipPrimary) {
    writeBeneficiaryGroup(w, 'Primary', bene.primary);
  }
  writeBeneficiaryGroup(w, 'Contingent', bene.contingent);
  w.notes(bene.notes);
  n += 1;

  return n;
}

export const buildPdsPdfBlob = (
  formData = {},
  { clientName = '', clientEmail = '' } = {},
) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const w = new PdfWriter(doc);

  w.title('Personal Data Sheet');
  w.subtitle([clientName, clientEmail].filter(Boolean).join(' | '));
  w.subtitle(`Generated ${new Date().toLocaleString()}`);

  const spousePresent = householdHasSpouse(formData);
  const household = formData.household || {};

  w.sectionHeader(1, 'Household');
  w.row('Has a spouse or partner', fmtYesNo(household.has_spouse));
  if (spousePresent && household.spouse_name) {
    w.row('Spouse / partner name', fmtText(household.spouse_name));
  }
  w.notes(household.notes);

  let next = writePerson(w, formData, '', 'Client', 2, spousePresent);

  if (spousePresent) {
    writePerson(w, formData, 'spouse', 'Spouse / Partner', next, spousePresent);
  }

  w.footer();
  return doc.output('blob');
};

export const exportPdsFormToPdf = (formData = {}, options = {}) => {
  const blob = buildPdsPdfBlob(formData, options);
  const filename = defaultPdsPdfFilename(options.clientName);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
